import { menuItems, getMenuItemsByRole } from '../../config/menuConfig';
import {
  mergeTicketingDepartmentsIntoMenu,
  filterSidebarMenuByRoutePermissions,
  buildVisibleSidebarMenu,
} from '../../config/menuSidebarUtils';

export {
  mergeTicketingDepartmentsIntoMenu,
  filterSidebarMenuByRoutePermissions,
  buildVisibleSidebarMenu,
};

/**
 * Json column on ad_User: { [routeHref]: { view?, edit?, delete? } }.
 */
export function getSavedRoutePermissions(user) {
  if (user == null) return {};
  const raw = user.sidebarRoutePermissions;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...raw };
  }
  return {};
}

function flatten(items, parents = [], out = []) {
  (items || []).forEach((item) => {
    if (!item || item.isHeader) return;
    const path = [...parents, item.name].filter(Boolean);
    if (item.href) {
      out.push({
        key: item.href,
        href: item.href,
        label: item.name,
        group: parents[0] || item.name,
        pathLabel: path.join(' > '),
      });
    }
    if (Array.isArray(item.subitems) && item.subitems.length > 0) {
      flatten(item.subitems, path, out);
    }
  });
  return out;
}

export function getSidebarPermissionModules() {
  const modules = flatten(menuItems);
  const dedup = new Map();
  modules.forEach((m) => {
    if (!dedup.has(m.key)) dedup.set(m.key, m);
  });
  return [...dedup.values()];
}

/** Matches sidebar links injected from the ticketing departments API (`Sidebar` `ticketingMergeDepartments`). */
export const TICKETING_DEPARTMENT_ROUTE_PREFIX = '/ticketing/department/';

export function hrefForTicketingDepartment(departmentId) {
  if (departmentId == null || departmentId === '') return null;
  return `${TICKETING_DEPARTMENT_ROUTE_PREFIX}${departmentId}`;
}

export function isTicketingDepartmentRoute(href) {
  return typeof href === 'string' && href.startsWith(TICKETING_DEPARTMENT_ROUTE_PREFIX);
}

function sortDepartmentsLikeSidebar(depts) {
  return [...(depts || [])]
    .filter((d) => d && d.isActive !== false)
    .sort((a, b) => {
      const oa = a.record_order ?? 999999;
      const ob = b.record_order ?? 999999;
      if (oa !== ob) return oa - ob;
      return (a.name || '').localeCompare(b.name || '');
    });
}

/**
 * One permission row per active ticketing department (same hrefs as the dynamic sidebar items).
 * @param {Array<{ id: string, name?: string, isActive?: boolean, record_order?: number }>} departments
 */
export function permissionModulesForTicketingDepartments(departments) {
  return sortDepartmentsLikeSidebar(departments).map((d) => {
    const href = hrefForTicketingDepartment(d.id);
    const name = d.name || 'Department';
    return {
      key: href,
      href,
      label: name,
      group: 'Ticketing',
      pathLabel: `Ticketing > ${name}`,
      isTicketingDepartment: true,
    };
  });
}

/**
 * Static sidebar modules plus one entry per ticketing department (Ticketing group: departments first, then static ticketing routes — same order as `Sidebar` merge).
 */
export function getSidebarPermissionModulesWithTicketingDepartments(departments) {
  const staticMods = getSidebarPermissionModules();
  const deptMods = permissionModulesForTicketingDepartments(departments);
  const ticketingStatic = staticMods.filter((m) => m.group === 'Ticketing');
  const other = staticMods.filter((m) => m.group !== 'Ticketing');
  return [...other, ...deptMods, ...ticketingStatic];
}

export function getDefaultPermissionsMapFromModules(modules) {
  const map = {};
  (modules || []).forEach((m) => {
    if (m?.href) map[m.href] = { view: true, edit: true, delete: true };
  });
  return map;
}

export function getDefaultPermissionsMap() {
  return getDefaultPermissionsMapFromModules(getSidebarPermissionModules());
}

/**
 * @param {object|null} user — auth user (expects `sidebarRoutePermissions`) or a plain `{ sidebarRoutePermissions }` for admin editing.
 * @param {Array<{ href: string }>|undefined} modulesForDefaults — when set (e.g. admin page with departments), defaults include those hrefs.
 */
export function resolvePermissionsForUser(user, modulesForDefaults) {
  const defaults =
    Array.isArray(modulesForDefaults) && modulesForDefaults.length > 0
      ? getDefaultPermissionsMapFromModules(modulesForDefaults)
      : getDefaultPermissionsMap();
  const saved = getSavedRoutePermissions(user);
  const merged = { ...defaults };
  Object.entries(saved).forEach(([route, val]) => {
    merged[route] = {
      view: val?.view !== false,
      edit: val?.edit !== false,
      delete: val?.delete !== false,
    };
  });
  return merged;
}

export function canViewRouteForUser(user, href) {
  if (!href) return true;
  const merged = resolvePermissionsForUser(user);
  return merged[href]?.view !== false;
}

function firstAccessibleHrefDepthFirst(items) {
  for (const item of items || []) {
    if (!item || item.isHeader) continue;
    if (item.subitems && item.subitems.length > 0) {
      const sub = firstAccessibleHrefDepthFirst(item.subitems);
      if (sub) return sub;
    }
    if (item.href) return item.href;
  }
  return null;
}

/**
 * First sidebar link the user is allowed to open (includes merged ticketing departments).
 * Matches menu order used in the sidebar. Falls back to `/` if nothing matches.
 */
export function getPostLoginRedirectPath(
  user,
  ticketingDepartments,
  userRole,
  hiddenHrefs = [],
) {
  const role = userRole ?? user?.role?.name ?? 'user';
  const base = getMenuItemsByRole(role);
  const merged = mergeTicketingDepartmentsIntoMenu(base, ticketingDepartments);
  const visible = buildVisibleSidebarMenu(merged, user, hiddenHrefs);
  return firstAccessibleHrefDepthFirst(visible) || '/';
}

/** Path prefixes allowed when returning the user to their pre-login URL (email links, bookmarks). Not used for /users etc. */
const SAFE_POST_LOGIN_RETURN_PREFIXES = [
  '/ticketing/',
  '/approval/',
  '/report-scheduling/',
  '/reports/',
];

/**
 * Whether `pathname` is safe to use as post-login redirect (same-origin app routes only).
 */
export function isSafePostLoginReturnPath(pathname) {
  if (pathname == null || typeof pathname !== 'string') return false;
  const p = pathname.trim();
  if (!p.startsWith('/') || p.includes('..')) return false;
  if (p.includes('//')) return false;
  return SAFE_POST_LOGIN_RETURN_PREFIXES.some((prefix) => p.startsWith(prefix));
}
